import React, { useEffect, useState } from 'react';
import { Col, Row, Form, InputNumber, FormInstance } from 'antd';
import { ShiftDurationInput } from './shift-duration-input';
import { StartDatetimeSelect } from './start-datetime-select';
import { UnitRoleSelect } from './unit-role-select';
import { ShiftTagSelect } from './shift-tag-select';
import { useLazyQuery, useQuery } from '@common/hooks';
import { INDEX_SHIFT_CONFIGS_QUERY } from '@scheduling/graphql';
import { RolesQuery, RolesResponse } from '@clinician-profile/graphql';
import { ErrorHandler, LoadingState } from '@common/components';
import moment from 'moment-timezone';
import { OrganizationUnitShiftConfig } from '@scheduling/graphql/types';

type OrganizationUnitShiftConfigResponse = {
  organizationUnitShiftConfigs: OrganizationUnitShiftConfig[];
};

export type CreateShiftFormValuesType = {
  shiftDuration: number[];
  organizationUnitId: string;
  roleId: string;
  startDate: string;
  startTime: string;
  openings: number;
  shiftGapTagIds: string[];
};
interface ShiftRequestFormProps {
  form: FormInstance;
  handleSubmit: (values: CreateShiftFormValuesType) => void;
  startDate?: string;
}

export function CreateShiftForm({
  form,
  handleSubmit,
  startDate,
}: ShiftRequestFormProps) {
  const [shiftConfigs, setShiftConfigs] = useState<
    OrganizationUnitShiftConfig[]
  >([]);

  const [selectedStartTime, setSelectedStartTime] = useState<
    string | undefined
  >(undefined);
  const [organizationUnitId, setOrganizationUnitId] = useState<string | null>(
    null
  );
  const [roleId, setRoleId] = useState<string | null>(null);

  const {
    loading: rolesLoading,
    error: rolesError,
    data: rolesData,
  } = useQuery<RolesResponse>(RolesQuery);

  const [
    shiftConfigQuery,
    { loading: loadingShiftConfigs, error: errorShiftConfigs },
  ] = useLazyQuery<OrganizationUnitShiftConfigResponse>(
    INDEX_SHIFT_CONFIGS_QUERY,
    {
      onCompleted: (data) => {
        const organizationUnitShiftConfigs =
          data.organizationUnitShiftConfigs?.map(
            (config: OrganizationUnitShiftConfig) => {
              const { startTime: startTime, endTime: endTime } = config;

              return {
                ...config,
                startTime: moment.parseZone(startTime),
                endTime: moment.parseZone(endTime),
              };
            }
          );

        setShiftConfigs(organizationUnitShiftConfigs || []);
      },
    }
  );

  useEffect(() => {
    if (organizationUnitId || roleId) {
      shiftConfigQuery({
        variables: {
          filters: { role: roleId, unit: organizationUnitId },
        },
      });
    }
  }, [organizationUnitId, roleId, shiftConfigQuery]);

  useEffect(() => {
    if (startDate) {
      form.setFieldsValue({ startDate: moment(startDate) });
    }
  }, [form, startDate]);

  if (rolesLoading) {
    return <LoadingState />;
  }

  if (rolesError) {
    return <ErrorHandler error={rolesError} log="ShiftForm" />;
  }

  if (errorShiftConfigs) {
    return <ErrorHandler error={errorShiftConfigs} log="ShiftForm" />;
  }

  const onFormValuesChange = (changed: {
    organizationUnitId: string;
    roleId: string;
  }) => {
    const newOrganizationUnitId = changed.organizationUnitId;
    const newRoleId = changed.roleId;

    if (newOrganizationUnitId || newRoleId) {
      setShiftConfigs([]);
      setSelectedStartTime(undefined);
      form.setFieldsValue({
        startTime: undefined,
        shiftDuration: undefined,
      });
    }

    if (newOrganizationUnitId) {
      setOrganizationUnitId(newOrganizationUnitId);
    }
    if (newRoleId) {
      setRoleId(newRoleId);
    }
  };

  return (
    <>
      <Form
        className="CreateShiftForm"
        form={form}
        onValuesChange={onFormValuesChange}
        onFinish={handleSubmit}
        layout="vertical"
      >
        <Row gutter={[8, 8]}>
          <UnitRoleSelect rolesData={rolesData?.roles} permittedDepartment />
          <StartDatetimeSelect
            form={form}
            shiftConfigs={shiftConfigs}
            onTimeSelect={setSelectedStartTime}
            startTime={selectedStartTime}
            loading={loadingShiftConfigs}
          />
          <ShiftDurationInput
            shiftConfigs={shiftConfigs}
            startTime={selectedStartTime}
            form={form}
            loading={loadingShiftConfigs}
          />
          <Col span={12}>
            <Form.Item
              label="Openings"
              name="openings"
              initialValue={1}
              rules={[
                {
                  required: true,
                  message: 'Please enter the number of openings',
                },
                {
                  type: 'number',
                  min: 1,
                  max: 99,
                  message: 'Please enter a number between 1 and 99',
                },
              ]}
            >
              <InputNumber
                size="large"
                style={{
                  width: '100%',
                }}
                defaultValue={1}
                min={1}
                max={99}
              />
            </Form.Item>
          </Col>
          <ShiftTagSelect />
        </Row>
      </Form>
      <style jsx>
        {`
          :global(.CreateShiftForm .ant-input-number),
          :global(.CreateShiftForm .ant-form-item-control-input-content)
            :global(.CreateShiftForm .ant-picker) {
            width: 100%;
          }
        `}
      </style>
    </>
  );
}
